import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Eye, CheckCircle } from 'lucide-react';
import api from '../../utils/api';

const SUBJECT_OPTIONS = [
  'Mathematics',
  'Science',
  'English',
  'Hindi',
  'Social Studies',
  'Computer Science',
  'Kannada',
  'Physical Education'
];

const CLASS_OPTIONS = Array.from({ length: 6 }, (_, i) => i + 5);
const SECTION_OPTIONS = ['A', 'B'];
const STATUS_OPTIONS = ['planned', 'completed', 'cancelled'];

const getStatusColor = (status) => {
  const colors = {
    'planned': '#3b82f6',
    'completed': '#10b981',
    'cancelled': '#ef4444'
  };
  return colors[status] || '#6b7280';
};

export default function LessonPlansPage() {
  const [lessonPlans, setLessonPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Filter state
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    subject: '',
    class: '',
    section: 'A',
    topic: '',
    objectives: '',
    content: '',
    activities: '',
    resources: '',
    homework: '',
    date: new Date().toISOString().split('T')[0],
    period: '',
    duration: ''
  });

  // Load lesson plans
  const loadLessonPlans = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const params = new URLSearchParams();
      if (selectedSubject) params.append('subject', selectedSubject);
      if (selectedClass) params.append('class', selectedClass);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedDate) params.append('date', selectedDate);

      const response = await api.get(`/lesson-plans?${params.toString()}`);
      const plansData = response.data.lessonPlans || response.data || [];
      setLessonPlans(Array.isArray(plansData) ? plansData : []);
      setMessage({ type: 'success', text: `Loaded ${plansData.length} lesson plans` });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load lesson plans'
      });
      setLessonPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLessonPlans();
  }, [selectedSubject, selectedClass, selectedStatus, selectedDate]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openAddModal = () => {
    setIsEditing(false);
    setFormData({
      subject: '',
      class: '',
      section: 'A',
      topic: '',
      objectives: '',
      content: '',
      activities: '',
      resources: '',
      homework: '',
      date: new Date().toISOString().split('T')[0],
      period: '',
      duration: ''
    });
    setShowAddModal(true);
  };

  const openEditModal = (plan) => {
    setIsEditing(true);
    setSelectedPlan(plan);
    setFormData({
      subject: plan.subject || '',
      class: plan.class || '',
      section: plan.section || 'A',
      topic: plan.topic || '',
      objectives: plan.objectives || '',
      content: plan.content || '',
      activities: plan.activities || '',
      resources: plan.resources || '',
      homework: plan.homework || '',
      date: plan.date || new Date().toISOString().split('T')[0],
      period: plan.period || '',
      duration: plan.duration || ''
    });
    setShowAddModal(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.subject || !formData.class || !formData.topic) {
      setMessage({ type: 'error', text: 'Please fill required fields' });
      return;
    }

    try {
      if (isEditing && selectedPlan?.id) {
        await api.put(`/lesson-plans/${selectedPlan.id}`, formData);
        setMessage({ type: 'success', text: 'Lesson plan updated successfully' });
      } else {
        await api.post('/lesson-plans', formData);
        setMessage({ type: 'success', text: 'Lesson plan created successfully' });
      }
      setShowAddModal(false);
      loadLessonPlans();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save lesson plan'
      });
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan?.id) return;

    try {
      await api.delete(`/lesson-plans/${selectedPlan.id}`);
      setMessage({ type: 'success', text: 'Lesson plan deleted successfully' });
      setShowDeleteConfirm(false);
      loadLessonPlans();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to delete lesson plan'
      });
    }
  };

  const handleMarkAsCompleted = async (plan) => {
    try {
      await api.put(`/lesson-plans/${plan.id}/complete`);
      setMessage({ type: 'success', text: 'Lesson plan marked as completed' });
      loadLessonPlans();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update lesson plan'
      });
    }
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowViewModal(false);
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '10px', color: '#1f2937' }}>
          Lesson Plans
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '20px' }}>Organize and manage lesson plans</p>

        {message.text && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '6px',
              marginBottom: '20px',
              backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: message.type === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`
            }}
          >
            {message.text}
          </div>
        )}

        <button
          onClick={openAddModal}
          style={{
            padding: '10px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px'
          }}
        >
          <Plus size={18} /> Add Lesson Plan
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}
      >
        <select
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            backgroundColor: 'white',
            fontSize: '14px'
          }}
        >
          <option value="">All Subjects</option>
          {SUBJECT_OPTIONS.map((subject) => (
            <option key={subject} value={subject}>
              {subject}
            </option>
          ))}
        </select>

        <select
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            backgroundColor: 'white',
            fontSize: '14px'
          }}
        >
          <option value="">All Classes</option>
          {CLASS_OPTIONS.map((cls) => (
            <option key={cls} value={cls}>
              Class {cls}
            </option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            backgroundColor: 'white',
            fontSize: '14px'
          }}
        >
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            backgroundColor: 'white',
            fontSize: '14px'
          }}
        />
      </div>

      {/* Lesson Plans Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px'
        }}
      >
        {loading ? (
          <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            Loading...
          </div>
        ) : lessonPlans.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            No lesson plans found
          </div>
        ) : (
          lessonPlans.map((plan, index) => (
            <div
              key={plan.id || index}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderTop: `4px solid ${getStatusColor(plan.status || 'planned')}`
              }}
            >
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>
                    {plan.topic}
                  </h3>
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>
                    {plan.subject} - Class {plan.class} {plan.section}
                  </p>
                </div>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    backgroundColor: getStatusColor(plan.status || 'planned') + '20',
                    color: getStatusColor(plan.status || 'planned'),
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {(plan.status || 'planned').charAt(0).toUpperCase() + (plan.status || 'planned').slice(1)}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '13px' }}>
                <div>
                  <p style={{ color: '#6b7280', marginBottom: '2px' }}>Date</p>
                  <p style={{ color: '#1f2937', fontWeight: '500' }}>{plan.date}</p>
                </div>
                <div>
                  <p style={{ color: '#6b7280', marginBottom: '2px' }}>Period</p>
                  <p style={{ color: '#1f2937', fontWeight: '500' }}>{plan.period || '-'}</p>
                </div>
                <div>
                  <p style={{ color: '#6b7280', marginBottom: '2px' }}>Duration</p>
                  <p style={{ color: '#1f2937', fontWeight: '500' }}>{plan.duration ? `${plan.duration} min` : '-'}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowViewModal(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    backgroundColor: '#e0e7ff',
                    color: '#4f46e5',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Eye size={14} /> View
                </button>
                {(plan.status || 'planned') !== 'completed' && (
                  <button
                    onClick={() => handleMarkAsCompleted(plan)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <CheckCircle size={14} /> Done
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => openEditModal(plan)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowDeleteConfirm(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50
          }}
          onClick={closeModals}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                {isEditing ? 'Edit Lesson Plan' : 'Add Lesson Plan'}
              </h2>
              <button
                onClick={closeModals}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSavePlan}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Select Subject</option>
                    {SUBJECT_OPTIONS.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                    Class
                  </label>
                  <select
                    name="class"
                    value={formData.class}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Select Class</option>
                    {CLASS_OPTIONS.map((cls) => (
                      <option key={cls} value={cls}>
                        Class {cls}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                    Section
                  </label>
                  <select
                    name="section"
                    value={formData.section}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    {SECTION_OPTIONS.map((section) => (
                      <option key={section} value={section}>
                        {section}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                    Topic
                  </label>
                  <input
                    type="text"
                    name="topic"
                    value={formData.topic}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                  Objectives
                </label>
                <textarea
                  name="objectives"
                  value={formData.objectives}
                  onChange={handleFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                  Content
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                  Activities
                </label>
                <textarea
                  name="activities"
                  value={formData.activities}
                  onChange={handleFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                  Resources
                </label>
                <textarea
                  name="resources"
                  value={formData.resources}
                  onChange={handleFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                  Homework
                </label>
                <textarea
                  name="homework"
                  value={formData.homework}
                  onChange={handleFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                    Period
                  </label>
                  <input
                    type="number"
                    name="period"
                    value={formData.period}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#1f2937' }}>
                    Duration (min)
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeModals}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#e5e7eb',
                    color: '#1f2937',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {isEditing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {showViewModal && selectedPlan && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50
          }}
          onClick={() => setShowViewModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                {selectedPlan.topic}
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Subject</p>
                <p style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{selectedPlan.subject}</p>
              </div>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Class - Section</p>
                <p style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  Class {selectedPlan.class} - {selectedPlan.section}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Date</p>
                <p style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{selectedPlan.date}</p>
              </div>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Status</p>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    backgroundColor: getStatusColor(selectedPlan.status || 'planned') + '20',
                    color: getStatusColor(selectedPlan.status || 'planned'),
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  {(selectedPlan.status || 'planned').charAt(0).toUpperCase() + (selectedPlan.status || 'planned').slice(1)}
                </span>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '6px' }}>Objectives</p>
                <p style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                  {selectedPlan.objectives || '-'}
                </p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '6px' }}>Content</p>
                <p style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                  {selectedPlan.content || '-'}
                </p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '6px' }}>Activities</p>
                <p style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                  {selectedPlan.activities || '-'}
                </p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '6px' }}>Resources</p>
                <p style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                  {selectedPlan.resources || '-'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '6px' }}>Homework</p>
                <p style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                  {selectedPlan.homework || '-'}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#e5e7eb',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px' }}>
              Delete Lesson Plan?
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
              Are you sure you want to delete this lesson plan? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#e5e7eb',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePlan}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
